import { useTheme } from "@/theme/ThemeProvider";
import { useStyles } from "@/theme/styles";
import { CaretDownIcon, CaretRightIcon } from "phosphor-react-native";
import React from "react";
import { Pressable, Text, View, ViewStyle } from "react-native";

type IconProps = {
  size?: number;
  color?: string;
  weight?: "thin" | "light" | "regular" | "bold" | "fill" | "duotone";
};

type Props = {
  /** Titel der Sektion */
  title: string;
  /** Optionales Icon */
  icon?: React.ComponentType<IconProps>;
  /** Optionaler Untertitel/Badge */
  subtitle?: string;
  /** Ist die Sektion offen? */
  expanded: boolean;
  /** Callback wenn Header geklickt wird */
  onToggle: () => void;
  /** Inhalt der Sektion */
  children: React.ReactNode;
  /** Optional: Style für den Container */
  style?: ViewStyle;
  /** Kompakter Gruppenkopf (z. B. CareSite in der Tierliste) */
  dense?: boolean;
};

/**
 * Aufklappbare Accordion-Sektion.
 *
 * Zeigt Header mit Titel + Caret, bei Klick wird expanded getoggelt.
 * Inhalt wird nur angezeigt wenn expanded=true.
 */
export function AccordionSection({
  title,
  icon: Icon,
  subtitle,
  expanded,
  onToggle,
  children,
  style,
  dense = false,
}: Props) {
  const { theme } = useTheme();
  const s = useStyles();
  const pad = dense ? theme.space.sm : theme.space.lg;
  const gap = dense ? theme.space.sm : 12;

  return (
    <View
      style={{
        backgroundColor: theme.color.surface,
        borderRadius: dense ? theme.radius.md : theme.radius.lg,
        overflow: "hidden",
        ...style,
      }}
    >
      <Pressable
        onPress={onToggle}
        style={[
          s.rowSpaced,
          {
            paddingVertical: dense ? 8 : theme.space.lg,
            paddingHorizontal: pad,
            backgroundColor: dense ? theme.color.bgStrong : theme.color.surface,
          },
        ]}
      >
        <View style={[s.row, { gap, flex: 1 }]}>
          {Icon && (
            <View
              style={[
                s.iconBgAccent,
                {
                  backgroundColor: expanded
                    ? theme.color.accentBg
                    : theme.color.bg,
                },
              ]}
            >
              <Icon
                size={dense ? 18 : 22}
                color={expanded ? theme.color.accent : theme.color.muted}
                weight={expanded ? "fill" : "regular"}
              />
            </View>
          )}

          <View
            style={{
              flex: 1,
              flexDirection: dense ? "row" : "column",
              alignItems: dense ? "center" : "flex-start",
              gap: dense ? theme.space.sm : 0,
            }}
          >
            <Text
              style={
                dense
                  ? {
                      color: theme.color.text,
                      fontSize: theme.fontSize.small,
                      fontWeight: "700",
                      flexShrink: 1,
                    }
                  : s.textBodyBold
              }
              numberOfLines={1}
            >
              {title}
            </Text>
            {subtitle ? (
              <Text
                style={{
                  color: theme.color.muted,
                  fontSize: theme.fontSize.tiny,
                  fontWeight: "600",
                  marginTop: dense ? 0 : 2,
                }}
                numberOfLines={1}
              >
                {dense ? `· ${subtitle}` : subtitle}
              </Text>
            ) : null}
          </View>
        </View>

        {expanded ? (
          <CaretDownIcon size={dense ? 16 : 20} color={theme.color.muted} />
        ) : (
          <CaretRightIcon size={dense ? 16 : 20} color={theme.color.muted} />
        )}
      </Pressable>

      {expanded && (
        <View
          style={[
            s.dividerTop,
            {
              paddingHorizontal: dense ? 0 : theme.space.lg,
              paddingBottom: dense ? 0 : theme.space.lg,
            },
          ]}
        >
          {children}
        </View>
      )}
    </View>
  );
}
